import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'rastyle_search_cache_v1';
const CATALOGUE_KEY = 'rastyle_search_catalogue_v1';
const EMPTY = { items: [], facets: null, total: 0, correctedQuery: null, expandedTerms: [], loading: false, error: null, isStale: false };

const readJson = (key, fallback) => {
  try { return JSON.parse(window.localStorage.getItem(key) || '') || fallback; }
  catch { return fallback; }
};

const writeJson = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch { /* storage can be unavailable in privacy mode */ }
};

export const searchCacheKey = (query, entityType = 'all') => `${entityType}:${query.trim().toLowerCase()}`;

export function searchCachedCatalogue(items, query, entityType = 'all', limit = 30) {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  return (Array.isArray(items) ? items : [])
    .filter((item) => entityType === 'all' || item.type === entityType)
    .map((item) => {
      const text = [item.title, item.summary, ...(item.tags || [])].filter(Boolean).join(' ').toLowerCase();
      const score = tokens.reduce((total, token) => total + (text.includes(token) ? 1 : 0), 0);
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ item }) => item);
}

function readCachedResult(query, entityType) {
  const cache = readJson(CACHE_KEY, {});
  const exact = cache[searchCacheKey(query, entityType)];
  if (exact?.items?.length) return exact;
  const items = searchCachedCatalogue(readJson(CATALOGUE_KEY, []), query, entityType);
  return items.length ? { items, total: items.length } : null;
}

function persistResult(query, entityType, payload) {
  const entry = { ...payload, cachedAt: Date.now() };
  const cache = readJson(CACHE_KEY, {});
  const key = searchCacheKey(query, entityType);
  delete cache[key];
  const nextCache = { [key]: entry, ...cache };
  writeJson(CACHE_KEY, Object.fromEntries(Object.entries(nextCache).slice(0, 40)));

  const existing = readJson(CATALOGUE_KEY, []);
  const catalogue = new Map(existing.map((item) => [`${item.type}:${item.id}`, item]));
  (payload.items || []).forEach((item) => {
    const itemKey = `${item.type}:${item.id}`;
    catalogue.delete(itemKey);
    catalogue.set(itemKey, item);
  });
  writeJson(CATALOGUE_KEY, Array.from(catalogue.values()).slice(-400));
}

export default function useUniversalSearch(query, entityType = 'all', delay = 250) {
  const [state, setState] = useState(EMPTY);
  const [retryVersion, setRetryVersion] = useState(0);
  const retry = useCallback(() => setRetryVersion((value) => value + 1), []);

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) {
      setState(EMPTY);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const params = { q: normalized, limit: 30 };
        if (entityType !== 'all') params.entity_type = entityType;
        let response;
        let requestError;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            response = await axios.get(`${API}/api/content-search`, { params, signal: controller.signal });
            break;
          } catch (error) {
            if (error?.code === 'ERR_CANCELED') return;
            requestError = error;
            if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 250));
          }
        }
        if (!response) throw requestError;
        const nextState = {
          items: response.data?.items || [],
          facets: response.data?.facets || null,
          total: response.data?.total || 0,
          correctedQuery: response.data?.corrected_query || null,
          expandedTerms: response.data?.expanded_terms || [],
          loading: false,
          error: null,
          isStale: false,
        };
        persistResult(normalized, entityType, nextState);
        setState(nextState);
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') return;
        const cached = readCachedResult(normalized, entityType);
        setState({
          ...EMPTY,
          ...(cached || {}),
          error,
          isStale: Boolean(cached),
        });
      }
    }, delay);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, entityType, delay, retryVersion]);

  return { ...state, retry };
}
